import styled from "styled-components";
import { useEffect, useRef } from "react";
import { Plus, Zap } from "lucide-react";
import { usePage, router, Link } from "@inertiajs/react";

import Toast from "./Toast";
import TopBarStatus from "./TopBarStatus";
import NavLoader from "./NavLoader";
import NightDateGuard from "./NightDateGuard";
import { wibTodayIso } from "./dateFormat";
import { theme } from "./theme";
import { useT } from "./i18n";
import DateNav from "./DateNav";
import Clock from "./Clock";

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

// Header + nav pinned to the top so navigation is always reachable while scrolling.
const Bar = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
`;

// Full-bleed colour bar; its content is centred to the same 1400px column as the page Content below
// (see BarInner), so the brand and page title share one left edge (and the right controls one right edge).
const TopBar = styled.header`
  background: ${theme.ink};
  color: #ffffff;
  border-bottom: 2px solid ${theme.gold};
`;

const TopBarInner = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0.75rem 1.25rem;
`;

const Center = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;

const Brand = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  font-weight: 700;
  font-size: 1.05rem;
  letter-spacing: 0.04em;
  color: ${theme.gold};
`;

const BrandSub = styled.span`
  font-weight: 400;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.75);
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.85rem;
`;

const LangToggle = styled.div`
  display: inline-flex;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  overflow: hidden;
`;

const LangButton = styled.button`
  border: 0;
  padding: 0.25rem 0.6rem;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  color: ${(props) => (props.$active ? theme.ink : "#ffffff")};
  background: ${(props) => (props.$active ? theme.gold : "transparent")};
`;

const AdminLink = styled.a`
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  font-size: 0.8rem;
  &:hover {
    color: ${theme.gold};
    text-decoration: underline;
  }
`;

const Nav = styled.nav`
  background: #ffffff;
  border-bottom: 1px solid ${theme.border};
`;

const NavInner = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0.5rem 1.25rem;
`;

// Pushes the shared date filter to the far right of the nav row, on its own from the page links.
const NavDate = styled.div`
  margin-left: auto;
`;

const NavLink = styled(Link)`
  padding: 0.4rem 0.85rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: none;
  color: ${(props) => (props.$active ? theme.ink : theme.muted)};
  background: ${(props) => (props.$active ? theme.goldSoft : "transparent")};
  &:hover {
    color: ${theme.ink};
  }
`;

// "Booking Baru" is a CTA, not a page tab — a solid gold pill with a + icon so it reads as an action
// and stands apart from the ghost nav links.
const NewBookingLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 800;
  text-decoration: none;
  color: #ffffff;
  background: ${(props) => (props.$active ? theme.goldDark : theme.gold)};
  &:hover {
    background: ${theme.goldDark};
  }
`;

// Xendit brand blue (#4373FC). A second action pill, deliberately a different hue from the gold
// "Booking Baru" so the two read as different kinds of action rather than one repeated control.
//
// The bolt is the app's existing shorthand for "this is Xendit" (see the payment pill in
// BookingDrawer). Their actual wordmark is a trademark and is not vendored here — the brand colour
// plus the established bolt identifies the provider without shipping a home-made imitation of it.
const XENDIT_BLUE = "#4373fc";
const XENDIT_BLUE_DARK = "#2f57cc";

const CustomPaymentLink = styled(Link)`
  align-items: center;
  background: ${(props) => (props.$active ? XENDIT_BLUE_DARK : XENDIT_BLUE)};
  border-radius: 999px;
  color: #ffffff;
  display: inline-flex;
  font-size: 0.85rem;
  font-weight: 800;
  gap: 0.35rem;
  padding: 0.4rem 0.9rem;
  text-decoration: none;
  &:hover {
    background: ${XENDIT_BLUE_DARK};
  }
`;

const Content = styled.main`
  flex: 1 1 auto;
  padding: 1.25rem;
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
`;

const PageShell = (props) => {
  const { t, lang, setLanguage } = useT();
  const page = usePage();
  const user = page.props.current_user || {};
  const barRef = useRef(null);

  // The date filter lives in the shared nav row (far right) and is app-wide: every frontdesk_v2 page
  // exposes a `date` prop, so the picker shows and persists across all four pages. Each page uses the
  // date its own way — Beranda/Peta Kamar/Catatan filter their data by it; Housekeeping shows live
  // room status (no date dimension) but still carries the value so the filter is never lost in transit.
  const pathname = page.url.split("?")[0];
  const activeDate = page.props.date;
  const showDateNav = Boolean(activeDate);

  const goToDate = (nextDate) =>
    router.get(
      pathname,
      { date: nextDate },
      { preserveScroll: true, preserveState: false },
    );

  // Carry a non-today filter across pages so switching tabs keeps the selected day; today needs no
  // param (it is every controller's server default).
  const datedHref = (path) =>
    activeDate && activeDate !== wibTodayIso()
      ? `${path}?date=${activeDate}`
      : path;

  // Publish the sticky bar's height as a CSS var so in-page sticky section headers can offset below
  // it (the bar height changes with the responsive top row), instead of relying on a magic number.
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) {
      return undefined;
    }
    const apply = () =>
      document.documentElement.style.setProperty(
        "--fd-bar-height",
        `${bar.offsetHeight}px`,
      );
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(bar);
    return () => observer.disconnect();
  }, []);

  return (
    <Page>
      <Bar ref={barRef}>
        <TopBar>
          <TopBarInner>
            <Brand>
              {t("app_name")}
              <BrandSub>Hebe by HostelNMC</BrandSub>
            </Brand>
            <Center>
              <TopBarStatus refreshMs={props.refreshMs} />
            </Center>
            <Right>
              <Clock />
              <LangToggle>
                <LangButton
                  type="button"
                  $active={lang === "id"}
                  onClick={() => setLanguage("id")}
                >
                  ID
                </LangButton>
                <LangButton
                  type="button"
                  $active={lang === "en"}
                  onClick={() => setLanguage("en")}
                >
                  EN
                </LangButton>
              </LangToggle>
              {user.name ? <span>{user.name}</span> : null}
              <AdminLink href="/admin">/admin</AdminLink>
            </Right>
          </TopBarInner>
        </TopBar>
        <Nav>
          <NavInner>
            <NavLink
              href={datedHref("/frontdesk_v2")}
              $active={
                page.url === "/frontdesk_v2" ||
                page.url.startsWith("/frontdesk_v2?")
              }
            >
              {t("nav_today")}
            </NavLink>
            <NavLink
              href={datedHref("/frontdesk_v2/rooms_grid")}
              $active={page.url.startsWith("/frontdesk_v2/rooms_grid")}
            >
              {t("nav_rack")}
            </NavLink>
            <NavLink
              href={datedHref("/frontdesk_v2/housekeeping")}
              $active={page.url.startsWith("/frontdesk_v2/housekeeping")}
            >
              {t("nav_hk")}
            </NavLink>
            <NavLink
              href={datedHref("/frontdesk_v2/shift_notes")}
              $active={page.url.startsWith("/frontdesk_v2/shift_notes")}
            >
              {t("nav_notes")}
            </NavLink>
            <NewBookingLink
              href="/frontdesk_v2/direct_booking"
              $active={page.url.startsWith("/frontdesk_v2/direct_booking")}
            >
              <Plus size={15} /> {t("nav_newbooking")}
            </NewBookingLink>
            <CustomPaymentLink
              href="/frontdesk_v2/custom_payments"
              $active={page.url.startsWith("/frontdesk_v2/custom_payments")}
            >
              <Zap size={14} aria-hidden="true" />
              {t("nav_custom_payment")}
            </CustomPaymentLink>
            {showDateNav ? (
              <NavDate>
                <DateNav date={activeDate} onChange={goToDate} />
              </NavDate>
            ) : null}
          </NavInner>
        </Nav>
      </Bar>
      <Content>{props.children}</Content>
      <Toast />
      <NavLoader />
      <NightDateGuard />
    </Page>
  );
};

export default PageShell;
